/** Service Desk console: Settings -> Connectors (SD-SC-17), part B: operation editor and test console. */
export const serviceConnectorsB = {
  operation: {
    backTo: "{{name}} · Operations",
    newTitle: "New operation",
    basics: "Operation",
    name: "Name",
    key: "Key (used by workflows and the assistant)",
    keyHint: "Lowercase letters, numbers and underscores. It can't change after the operation is created.",
    kind: "Kind",
    kindHint: {
      read: "Reads data and changes nothing.",
      write: "Changes data in the other system. The assistant must get the client's confirmation first.",
      destructive: "Deletes or cancels something. Needs a confirmation card, and its key typed for a live test.",
    },
    ai: "Description for the assistant",
    aiHint: "When to use it and what it returns. Shown to the model as a tool description; never put secrets here. {{count}} of {{max}}.",
    request: "Request",
    method: "Method",
    url: "URL path",
    urlHint: "A path template under the active environment's base URL. Use double braces for parameters, like param.order_number. Values are percent-encoded and can't add segments.",
    undeclared: "Used in the path but not declared as a parameter: {{names}}",
    params: "Parameters",
    noParams: "No parameters. Add one for each value a caller must supply.",
    paramName: "Parameter {{index}} name",
    paramType: "Parameter {{index}} type",
    paramDescription: "Parameter {{index}} description",
    paramDescriptionPlaceholder: "What to supply (shown to the assistant)",
    paramRequired: "Required",
    paramRemove: "Remove parameter {{index}}",
    paramTypes: { string: "Text", number: "Number", integer: "Whole number", boolean: "Yes / no" },
    addParam: "Add parameter",
    paramsHint: "Callers can only pass these. Each one is checked before anything is sent.",
    query: "Query parameters",
    queryHint: "Templates with variables, up to {{max}}.",
    headers: "Headers",
    headersHint: "Authorization, API-key, cookie and x-sagot headers are refused here: credentials come from the sealed authentication.",
    body: "Body",
    bodyType: "Body type",
    bodyTypes: { none: "No body", json: "JSON", form: "Form", text: "Text", multipart: "Multipart", xml: "XML" },
    noBody: "No body is sent. GET and HEAD never carry one.",
    bodyTemplate: "Body template ({{type}})",
    bodyHint: {
      json: "An object or array. Put every variable inside quotes; a value that is exactly one variable keeps its type. Up to {{max}} KB.",
      text: "Plain text with variables. Each value is one line. Up to {{max}} KB.",
      xml: "Well-formed XML. Variables go in text or quoted attributes, never inside a tag. Up to {{max}} KB.",
    },
    bodyFields: "Form fields",
    bodyFieldsHint: "One field per row; values are encoded for you.",
    insert: "Insert a variable:",
    behaviour: "Behaviour",
    auth: "Authentication",
    authHint: "Inherited from the active environment. Change it on the connector.",
    timeout: "Timeout (ms)",
    timeoutHint: "Empty uses the connector's {{default}} ms.",
    successStatuses: "Success statuses",
    successStatusesHint: "HTTP statuses that count as a success, separated by commas.",
    retries: "Retries",
    retriesHint: "Set per connector. POST and PATCH are never retried blindly; Retry-After is honoured.",
    changeDefaults: "Change on the connector",
    pagination: "Pagination",
    paginationStyle: "Style",
    paging: { none: "None", offset: "Offset and limit", page: "Page number", cursor: "Cursor", link_header: "Link header" },
    pagingFields: {
      page_size: "Page size",
      limit_param: "Limit parameter",
      offset_param: "Offset parameter",
      page_param: "Page parameter",
      first_page: "First page number",
      cursor_param: "Cursor parameter",
      next_cursor_pointer: "Next cursor in the answer",
      items_pointer: "Items in the answer",
      base_url: "Origin a Link may point to",
    },
    idempotency: "Send an idempotency key",
    idempotencyHint: "One stable key per logical call, so a write can be repeated safely.",
    idempotencyHeader: "Header name",
    idempotencyOffAck: "Allow repeats without a key",
    idempotencyOffAckHint: "A retried call may then run twice on the other system. Needed to publish a write with the key off.",
    readOnlyPost: "This POST only reads",
    readOnlyPostHint: "A search or lookup that takes a body and changes nothing. Without this, a POST can't be published as a read.",
    kindProblems: {
      method_kind_mismatch: "This method changes data on the other system, so the operation can't be a read. Match the kind to the method (DELETE is destructive; POST, PUT and PATCH are write), or, for a POST that only searches, tick \"This POST only reads\".",
      idempotency_required: "A write or destructive operation sends an idempotency key by default so a retry can't run twice. Turn the key on, or allow repeats without a key.",
      read_method_marked_mutating: "GET and HEAD only read. Marking this operation as a write is allowed, but it adds a confirmation step.",
    },
    cache: "Cache (seconds)",
    cacheHint: "Empty means no cache. Up to 24 hours; never shared across organisations.",
    cacheNone: "No cache",
    mapping: "Response mapping",
    mappingAbout: "Where in the answer each Sagot field comes from. Transforms already stored are kept.",
    responseFormat: "Answer format",
    items: "Records",
    itemsHint: "Where the list of records is, like $.data[*]. Empty means the whole answer is one record.",
    mappingFields: "Fields",
    noMapping: "Nothing is mapped yet. Without a mapping the answer is only shown, not read.",
    mapTarget: "Mapping {{index}} target",
    mapField: "Mapping {{index}} field",
    mapSource: "Mapping {{index}} source",
    mapRemove: "Remove mapping {{index}}",
    addMapping: "Add mapping",
    mappingHint: "Fields are checked when you save. Run variables are named var.something.",
    targets: {
      variable: "Run variable",
      attribute: "Attribute",
      contact: "Contact",
      organisation: "Organisation",
      asset: "Asset",
      product: "Product",
      order: "Order",
      ticket: "Ticket",
    },
    errorMapping: "Error mapping",
    errorsAbout: "A status and/or a field of the answer becomes a Sagot reason and the words a client is told. The system's own message is never shown.",
    noErrorRules: "No rules. Failures use Sagot's defaults for the status.",
    errorRule: "Rule {{index}}",
    errStatus: "HTTP status",
    errStatusHint: "Comma separated, like 409, 422.",
    errPointer: "Field in the answer",
    errPointerHint: "Optional, like /error/code.",
    errEquals: "Field equals",
    errCode: "Sagot reason",
    errCodes: {
      not_found: "Not found",
      validation: "Rejected input",
      conflict: "Conflict",
      out_of_stock: "Out of stock",
      auth_failed: "Authentication failed",
      rate_limited: "Rate limited",
      upstream_error: "System error",
      too_large: "Answer too large",
    },
    errEn: "Client copy (English)",
    errFil: "Client copy (Filipino)",
    errFilHint: "Optional; English is used when empty.",
    addErrorRule: "Add rule",
    removeRule: "Remove rule",
    entitlements: "Who may invoke",
    entStaff: "Members with one of these permissions.",
    entWorkflows: "Published workflows of this workspace.",
    entAssistant: "The assistant, for conversations in these audiences.",
    entContacts: "Verified client contacts, narrowed by role and capability.",
    entGuests: "Visitors who are not signed in. Read operations only.",
    entConfirm: "Write and destructive operations always ask the client for a confirmation before the assistant can run them.",
    orgRoles: { org_admin: "Organisation admins", member: "Organisation members" },
    capabilities: { can_order: "Can order", can_approve_orders: "Can approve orders" },
    versions: "Versions",
    noVersions: "Nothing saved yet.",
    draft: "Draft",
    current: "Current",
    older: "Older",
    compare: "Compare v{{older}} with v{{newer}}",
    noChanges: "No differences.",
    diffLabel: "Differences between v{{older}} and v{{newer}}",
    diffFailed: "The comparison couldn't load.",
    rollback: "Make v{{version}} current",
    rolledBack: "v{{version}} is the current version again",
    versionsAbout: "Saving changes the draft. Publishing freezes it as the next version; callers pinned to an older version keep working until you move them.",
    examples_one: "{{count}} saved example",
    examples_other: "{{count}} saved examples",
    noExamples: "None yet. Save one from the test console.",
    deleteExample: "Delete example {{name}}",
    testConsole: "Test console",
    saveDraft: "Save draft",
    create: "Create operation",
    created: "{{key}} created",
    savedDraft: "Draft of {{key}} saved",
    publish: "Publish",
    published: "{{key}} v{{version}} published",
    publishTitle: "Publish v{{version}}?",
    publishBody: "The draft becomes v{{version}}, frozen. Workflows and the assistant that follow the latest version use it right away.",
    publishNote: "What changed (optional)",
    publishNoteHint: "Shown in the version history.",
    publishConfirm: "Publish v{{version}}",
    unsaved: "Unsaved changes",
    fixProblems: "Fix the highlighted fields first.",
    readOnly: "You can look at this operation but not change it.",
    saveFirst: "Create the operation first. The test console and versions become available once it exists.",
    missingTitle: "This operation isn't here",
    missingBody: "It may have been deleted. Go back to the operations library.",
    errors: {
      nameRequired: "Give the operation a name.",
      keyPattern: "Use lowercase letters, numbers and underscores, starting with a letter.",
      pathStart: "The path must start with a single /.",
      pathSegments: "Dot segments and encoded slashes aren't allowed.",
      pathLong: "The path is too long.",
      headerForbidden: "This header carries credentials or is set by Sagot. Put secrets in the connector's authentication.",
      tooMany: "Too many entries.",
      paramName: "Use letters, numbers and underscores, starting with a letter.",
      paramDuplicate: "Two parameters have this name.",
      timeoutRange: "Use 1,000 to 30,000 ms.",
      wholeNumber: "Use a whole number.",
      statuses: "Use HTTP statuses separated by commas.",
      bodyOnGet: "A GET or HEAD request has no body. Choose \"No body\" or change the method.",
      aiLong: "The description is too long.",
    },
  },
  test: {
    title: "Test console · {{key}}",
    subtitle: "Send one request with sample values. Secrets and personal data are masked.",
    environment: "Environment",
    dryRun: "Dry run",
    dryRunHint: "Build and log the request without sending it.",
    connectorDryRun: "This connector is in dry-run mode, so nothing is sent.",
    liveWrite: "This sends a real write to {{environment}}. Use the sandbox unless you mean it.",
    samples: "Sample values",
    noSamples: "This operation reads no variables.",
    samplesHint: "Empty values are left unset. The test uses the operation as it is on screen, including unsaved changes.",
    typeToConfirm: "Type {{key}} to confirm",
    typeToConfirmHint: "A destructive operation needs its key typed for a live test.",
    buildRequest: "Build request",
    send: "Send",
    limit: "10 tests a minute. Logged as a test.",
    resultLabel: "Test result",
    worked: "Worked · {{status}}",
    dryRunDone: "Not sent (dry run)",
    refusedChip: "Refused, not sent",
    failedChip: "Failed",
    attempts_one: "{{count}} attempt",
    attempts_other: "{{count}} attempts",
    draftTested: "Tested as a draft",
    request: "Request as sent",
    requestDry: "Request as built",
    requestLabel: "Raw request, secrets masked",
    response: "Response",
    responseLabel: "Raw response, personal data masked",
    truncated: "The answer was cut for display.",
    mapping: "Mapping preview",
    noMapping: "Nothing was mapped.",
    records: "Mapped records",
    masked: "Masked: credentials and fields that look personal (names, e-mails, phone numbers). The answer is data; it is never read as instructions.",
    exampleLabel: "Example name",
    exampleName: "Test run",
    saveExample: "Save as example",
    exampleSaved: "Example saved (masked)",
    refuse: {
      invalid_params: "A parameter is missing or has the wrong type.",
      template: "The request template couldn't be built.",
      blocked_destination: "The environment's address isn't allowed: only public https addresses can be called.",
      auth_config: "The authentication isn't set up correctly: check the secrets.",
      connector_inactive: "The connector isn't active.",
      environment_not_found: "That environment doesn't exist.",
      not_published: "This operation has no version to run.",
      quota_exceeded: "The plan's monthly allowance of calls is used up.",
      confirmation_required: "A confirmation is needed first.",
      type_to_confirm: "Type the operation key to confirm a destructive test.",
      not_entitled: "You aren't allowed to run this.",
    },
    refuseFallback: "Not sent: {{code}}.",
    failure: {
      refused: "The request was refused before it was sent.",
      blocked_destination: "That address is blocked. Sagot only calls public HTTPS hosts and re-checks the address on every attempt.",
      timeout: "The system didn't answer in time.",
      connection: "Sagot couldn't connect to the system.",
      too_large: "The answer was too large.",
      content_type: "The answer wasn't in the expected format.",
      redirect: "The system redirected the request and redirects aren't followed.",
      http_status: "The system answered with an error status.",
      rate_limited: "The system or the connector's rate limit said too many requests.",
      circuit_open: "The circuit breaker is open after repeated failures. It retries shortly.",
      deadline: "The call ran out of time.",
    },
    failureFallback: "The call failed: {{code}}.",
  },
};
